/* ==========================================================================
   Winkelwagen — de installatie als één regel.

   De installatie staat als meerdere regels in de winkelwagen (pakket plus
   meerwerk), maar wordt als één regel getoond (snippets/flux-cart-installatie).
   "Verwijderen" haalt die regels in één keer weg: los weghalen zou een
   installatie achterlaten die niet meer bij de situatie past.

   De plusknoppen in de kaart ernaast (snippets/flux-cart-upsell) leggen een
   product er direct bij.

   Dawn levert de winkelwagen als secties aan; na het bijwerken vragen we
   dezelfde secties opnieuw op en zetten we ze terug, zoals Dawn's eigen
   cart.js dat doet.
   ========================================================================== */

(() => {
  const SECTIES = ['cart-drawer', 'main-cart-items', 'main-cart-footer', 'cart-icon-bubble'];

  /* Dawn-bug. Zet je de hoeveelheid onder het minimum — de minknop bij één —
     dan zet cart.js het veld terug op de oude waarde. Het zoekt daarvoor naar
     #Quantity-N, maar in de lade heet dat veld #Drawer-quantity-N. Het vindt
     dus niets, valt over null, en de lade blijft halverwege bijgewerkt achter:
     geen regels, geen lege-winkelwagentekst.

     Zelfde methode, maar met beide id's. cart-drawer-items erft van cart-items,
     dus één patch dekt de pagina en de lade. */
  customElements.whenDefined('cart-items').then((Klasse) => {
    Klasse.prototype.resetQuantityInput = function (id) {
      const veld = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
      if (!veld) return;
      veld.value = veld.getAttribute('value');
      this.isEnterPressed = false;
    };
  });

  /* Waar hoort het antwoord terug? De sectie-id in de DOM is niet 'cart-items'
     maar 'template--123__cart-items' — Shopify zet het sjabloon ervoor. Zoeken
     op shopify-section-<naam> levert dus niets op, en dan bleef het bedrag op de
     afrekenknop staan terwijl de winkelwagen allang bijgewerkt was.

     Daarom zoeken we het vaste element binnenin (dat houdt zijn kale id) en
     pakken van daaruit de sectie eromheen. De lade is de uitzondering: die
     vervangen we van binnen, anders gaat het open paneel dicht. */
  const PLEKKEN = {
    'main-cart-items': { anker: () => document.getElementById('main-cart-items'), sectie: true },
    'main-cart-footer': { anker: () => document.getElementById('main-cart-footer'), sectie: true },
    'cart-icon-bubble': { anker: () => document.getElementById('cart-icon-bubble'), sectie: false },
    'cart-drawer': { anker: () => document.querySelector('.cart-drawer .drawer__inner'), sectie: false, deel: '.drawer__inner' },
  };

  const sectieVan = (el) => el?.closest('[id^="shopify-section"]') || el;

  function zetSecties(secties) {
    for (const naam of SECTIES) {
      const html = secties?.[naam];
      const plek = PLEKKEN[naam];
      const anker = plek?.anker();
      if (!html || !anker) continue;

      const doel = plek.sectie ? sectieVan(anker) : anker;
      const nieuw = new DOMParser().parseFromString(html, 'text/html');
      let bron;
      if (plek.deel) {
        bron = nieuw.querySelector(plek.deel);
      } else if (plek.sectie) {
        bron = sectieVan(nieuw.getElementById(anker.id));
      } else {
        // Het bolletje: de sectie heeft binnenin geen eigen id, dus pakken we
        // de sectiewikkel uit het antwoord.
        bron = nieuw.getElementById(`shopify-section-${naam}`) || nieuw.getElementById(anker.id) || nieuw.body;
      }
      if (bron) doel.innerHTML = bron.innerHTML;
    }
  }

  async function verwijder(sleutels, knop) {
    const updates = Object.fromEntries(sleutels.map((k) => [k, 0]));
    knop.setAttribute('aria-busy', 'true');
    knop.disabled = true;

    try {
      const antwoord = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: SECTIES, sections_url: window.location.pathname }),
      });
      const kar = await antwoord.json();
      zetSecties(kar.sections);

      // Dawn's eigen onderdelen (prijs in de kop, lege winkelwagen) luisteren hier.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'flux-cart', cartData: kar });
      }
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', kar.item_count === 0);
    } catch {
      window.location.reload(); // liever een verse pagina dan een verkeerd beeld
    }
  }

  async function toevoegen(id, knop) {
    knop.setAttribute('aria-busy', 'true');
    knop.disabled = true;
    try {
      const antwoord = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: SECTIES, sections_url: window.location.pathname }),
      });
      const kar = await antwoord.json();
      zetSecties(kar.sections);
      const nieuweKar = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'flux-cart', cartData: nieuweKar });
      }
    } catch {
      window.location.reload();
    }
  }

  /* De installatie hoort bij de laadpaal: gaat de laadpaal weg, dan gaat de
     installatie mee. Anders blijven er regels achter die nergens meer getoond
     worden — wel een bedrag, wel een bolletje, geen product in beeld. */
  function installatieErbij(rij) {
    const set = rij?.dataset.fluxInstallatieSet;
    const eigen = rij?.dataset.fluxRegel;
    if (!set || !eigen) return null;
    const groep = rij
      .closest('table, tbody')
      ?.querySelector(`.flux-cart-installatie[data-flux-installatie-set="${CSS.escape(set)}"] [data-flux-installatie-weg]`);
    const vanDeSet = groep ? groep.dataset.fluxInstallatieWeg.split(' ').filter(Boolean) : [];
    return [eigen, ...vanDeSet];
  }

  document.addEventListener('click', (e) => {
    const verwijderknop = e.target.closest('cart-remove-button a, cart-remove-button button');
    if (verwijderknop) {
      const sleutels = installatieErbij(verwijderknop.closest('[data-flux-installatie-set]'));
      if (sleutels && sleutels.length > 1) {
        e.preventDefault();
        e.stopImmediatePropagation();
        verwijder(sleutels, verwijderknop);
        return;
      }
    }

    const weg = e.target.closest('[data-flux-installatie-weg]');
    if (weg) {
      e.preventDefault();
      const sleutels = weg.dataset.fluxInstallatieWeg.split(' ').filter(Boolean);
      if (sleutels.length) verwijder(sleutels, weg);
      return;
    }
    const plus = e.target.closest('[data-flux-toevoegen]');
    if (plus) {
      e.preventDefault();
      toevoegen(plus.dataset.fluxToevoegen, plus);
    }
  });
})();
